/** @jsx jsx */
import React, {
	forwardRef,
	useCallback,
	useEffect,
	useRef,
	type PropsWithChildren,
	type ComponentType,
} from 'react';
import { cssMap, jsx } from '@compiled/react';
import noop from 'lodash/noop';
import { EngagementSpotlight as EngagementSpotlightDI } from '@atlassiansox/engagekit-ts';
import { SpotlightTarget as SpotlightTargetDI } from '@atlaskit/onboarding';
import { JSErrorBoundary as JsErrorBoundaryDI } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { FormattedDate, useIntl } from '@atlassian/jira-intl';
import { FormattedRelativeV2 as FormattedRelative } from '@atlassian/jira-intl/src/v2/formatted-relative.tsx';
import { MENU_ID } from '@atlassian/jira-navigation-apps-common/src/constants.tsx';
import { useTopMenus as useTopMenusDI } from '@atlassian/jira-navigation-apps-common/src/controllers/top-menus/index.tsx';
import { testIdGenerate } from '@atlassian/jira-navigation-apps-common/src/utils/test-id.tsx';
import { getWillShowNav4 } from '@atlassian/jira-navigation-apps-sidebar-nav4-rollout-core/src/common/utils/get-will-show-nav4/index.tsx';
import { useNavigationViewPlansPermission as useNavigationViewPlansPermissionDI } from '@atlassian/jira-plans-item-visibility/src/index.tsx';
import { useTenantContext as useTenantContextDI } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';
import { lazy } from '@atlassian/react-loosely-lazy';
import { useRouterActions as useRouterActionsDI } from '@atlassian/react-resource-router';
import { useConfluenceReactivationExperiment } from '@atlassian/jira-confluence-reactivation-experiment/src/services/user-eligibility-check/index.tsx';
import { NAVIGATION_ITEM_ID, SEARCH_SPOTLIGHT_KEY } from '../../common/constants.tsx';
import { TopLevelErrorBoundary } from '../../common/ui/error-boundary/index.tsx';
import { useNavigationItemAnalytics as useNavigationItemAnalyticsDI } from '../../controllers/navigation-item-analytics/main.tsx';
import { useTheme as useThemeDI } from '../../controllers/theme/main.tsx';
import { ClientProvider as ClientProviderDI } from './client-provider/main.tsx';
import { ExtensibleCrossProductSearchDialog as ExtensibleCrossProductSearchDialogDI } from './extensible-dialog/main.tsx';
import messages from './messages.tsx';
import { searchDialogMetric } from './performance-metrics/index.tsx';
import type { SearchProps, QuickFindProps } from './types.tsx';
import { useConfluenceAvailabilityExperiment } from './confluence-availability-experiment/main.tsx';

type HTMLElementRefShape = HTMLInputElement | null | undefined;
type HTMLInputElementRef =
	| {
			current: HTMLElementRefShape;
	  }
	| null
	| undefined;

export const useEffectFocusOnOpenDI = (ref: HTMLInputElementRef, isOpen: boolean) => {
	useEffect(() => {
		if (isOpen) {
			// Prevent keypress being passed to input
			setTimeout(() => {
				ref && ref.current && ref.current.focus();
			}, 0);
		}
	}, [isOpen, ref]);
};

const styles = cssMap({
	nav4SearchInputWrapper: {
		width: '100%',
		maxWidth: '780px',
	},
	nav4SearchInputOverrider: {
		width: '100%',
	},
	searchInputWrapper: {
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		position: 'relative',
		minWidth: '1px',
	},
	searchInputOverrider: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors
		"input:not([type='checkbox']):not([type='radio'])": {
			borderBottomRightRadius: '6px',
		},
	},
});

// eslint-disable-next-line jira/deprecations/no-rll-client-async-experiences
const LazyQuickFind = lazy<ComponentType<PropsWithChildren<QuickFindProps>>>(
	() =>
		import(/* webpackChunkName: "async-atlassian-navigation-quick-find" */ './quick-find').then(
			(m) => m.QuickFind,
		),
	{ ssr: false },
);

const Search = forwardRef<HTMLInputElement, SearchProps>(
	(
		{
			ExtensibleCrossProductSearchDialog = ExtensibleCrossProductSearchDialogDI,
			ClientProvider = ClientProviderDI,
			EngagementSpotlight = EngagementSpotlightDI,
			SearchInputOverrider = SearchInputOverriderDI,
			SearchInputWrapper = SearchInputWrapperDI,
			SpotlightTarget = SpotlightTargetDI,
			useEffectFocusOnOpen = useEffectFocusOnOpenDI,
			useNavigationItemAnalytics = useNavigationItemAnalyticsDI,
			useRouterActions = useRouterActionsDI,
			useTenantContext = useTenantContextDI,
			useNavigationViewPlansPermission = useNavigationViewPlansPermissionDI,
			// @ts-expect-error - TS2339 - Property 'useTheme' does not exist on type 'SearchProps'.
			useTheme = useThemeDI,
			useTopMenus = useTopMenusDI,
			JsErrorBoundary = JsErrorBoundaryDI,
		}: SearchProps,
		forwardedInputRef,
	) => {
		const testId = testIdGenerate('secondary-actions', 'search');
		const inputRef = useRef<HTMLInputElement>();
		const tenantContext = useTenantContext();
		const { cloudId, baseUrl, isAnonymous, appPermissions, atlassianAccountId } = tenantContext;
		const { formatMessage } = useIntl();
		const theme = useTheme();
		const [isSearchMenuOpen, { on: openMenu, off: closeMenu }] = useTopMenus(MENU_ID.SEARCH);
		const triggerAnalytics = useNavigationItemAnalytics({
			navigationItemId: NAVIGATION_ITEM_ID.SEARCH,
		});
		const [hasEditOrViewPlanPermission] = useNavigationViewPlansPermission();
		const onOpen = useCallback(() => {
			openMenu();
			triggerAnalytics();
		}, [openMenu, triggerAnalytics]);

		const withUpdatedLabel = (formattedDate: string) => (
			<>{`${formatMessage(messages.updated)} ${formattedDate}`}</>
		);

		const formatDate = (lastModified: string) => {
			// @ts-expect-error - TS2363 - The right-hand side of an arithmetic operation must be of type 'any', 'number', 'bigint' or an enum type.
			const daysElapsed = (Date.now() - new Date(lastModified)) / (1000 * 60 * 60 * 24);

			if (daysElapsed < 8) {
				return <FormattedRelative value={lastModified}>{withUpdatedLabel}</FormattedRelative>;
			}

			return (
				<FormattedDate year="numeric" month="short" day="2-digit" value={lastModified}>
					{withUpdatedLabel}
				</FormattedDate>
			);
		};

		const { push } = useRouterActions();

		const onNavigate = useCallback(
			// @ts-expect-error - TS7006 - Parameter 'event' implicitly has an 'any' type.
			(href: string, event) => {
				if (!(event.ctrlKey || event.metaKey) || fg('jira_rovo_in_quick_search')) {
					// ProductSearchDialog's onNavigate callback doesn't give us the `searchSessionId`
					// It also does not reliably provide us with an <a> tag to source the href from
					// So, where needed, we'll source the href by traversing up the DOM
					const url = event?.target?.href ?? event.target?.closest?.('a')?.href ?? href;

					push(url);
				}

				// NOTE: For whatever reason, `preventDefault` must occur after the `push`
				event.preventDefault();
			},
			[push],
		);

		// @ts-expect-error - Type '(instance: HTMLElementRefShape) => void' is not assignable to type '{ current: HTMLInputElement | null | undefined; }'
		useEffectFocusOnOpen(forwardedInputRef || inputRef, isSearchMenuOpen);

		const features = {
			hasSoftwareAccess: appPermissions.hasSoftwareAccess,
			hasAdvancedRoadmapsAccess: hasEditOrViewPlanPermission,
			issueNavigatorTransitionOn: true,
			isNINSmartQuerySPATransitionOn: true,
			isReportedByMeFilterEnabled: true,
			isPreviewOnJiraSearchResultsEnabled: true,
			enableIssueLabelsFilter: true,
			enableDateFromFilter: true,
		};

		const dialogProps = {
			isExpanded: isSearchMenuOpen,
			onOpen,
			onClose: closeMenu,
			onNavigate,
			jiraFeatures: features,
			formatDate,
			dialogFeatures: undefined,
			theme: theme?.mode?.search,
			pageLoadMetrics: {
				metric: searchDialogMetric,
				customDataGetter: noop,
			},
			primaryProduct: 'jira',
		};

		const {
			isEligibleInactiveUser,
			isEligibleNewUser,
			nudgeShown,
			setAllowConfluenceAccessCache: setAllowConfluenceAccessCacheReactivation,
		} = useConfluenceReactivationExperiment(cloudId, isSearchMenuOpen);

		const {
			isEligible: isConfluenceAvailabilityExperimentEligible,
			tabClicked: isConfluenceAvailabilityExperimentTabClicked,
			setAllowConfluenceAccessCache,
		} = useConfluenceAvailabilityExperiment(cloudId, isSearchMenuOpen);

		if (fg('rovo_search_jira_enable_quick_find')) {
			const isNav4Enabled = getWillShowNav4();
			return (
				<JsErrorBoundary id="jira-quick-find" packageName="search-page" fallback="unmount">
					<LazyQuickFind
						isNav4Enabled={isNav4Enabled}
						cloudId={cloudId}
						userId={atlassianAccountId || null}
						onNavigate={onNavigate}
					/>
				</JsErrorBoundary>
			);
		}

		if (getWillShowNav4()) {
			return (
				<JsErrorBoundary
					id="jira-quick-search"
					packageName="product-search-dialog"
					fallback="unmount"
				>
					<ClientProvider
						cloudId={cloudId}
						baseUrl={baseUrl}
						isAnonymous={isAnonymous}
						atlassianAccountId={atlassianAccountId}
					>
						{({ aggregatorUrl }) => (
							<SearchInputWrapper data-testid={testId}>
								<SearchInputOverrider>
									<ExtensibleCrossProductSearchDialog
										{...dialogProps}
										cloudId={cloudId}
										aggregatorUrl={aggregatorUrl}
										// @ts-expect-error - TS2322 - Type 'MutableRefObject<unknown> | ((instance: SearchProps | null) => void)' is not assignable to type 'HTMLInputElementRef'.
										forwardRef={forwardedInputRef || inputRef}
										confluenceReactivationExperiment={{
											isEligibleInactiveUser,
											isEligibleNewUser,
											nudgeShown,
											setAllowConfluenceAccessCache: setAllowConfluenceAccessCacheReactivation,
										}}
										confluenceAvailabilityExperiment={{
											isEligible: isConfluenceAvailabilityExperimentEligible,
											isTabClicked: isConfluenceAvailabilityExperimentTabClicked,
											setAllowConfluenceAccessCache,
										}}
									/>
								</SearchInputOverrider>
							</SearchInputWrapper>
						)}
					</ClientProvider>
				</JsErrorBoundary>
			);
		}

		return (
			<JsErrorBoundary
				id="jira-quick-search"
				packageName="product-search-dialog"
				fallback="unmount"
			>
				<ClientProvider
					cloudId={cloudId}
					baseUrl={baseUrl}
					isAnonymous={isAnonymous}
					atlassianAccountId={atlassianAccountId}
				>
					{({ aggregatorUrl }) => (
						<SearchInputWrapper data-testid={testId}>
							<SearchInputOverrider>
								<SpotlightTarget name={SEARCH_SPOTLIGHT_KEY}>
									<div // We wrap the dialog in a div to make the spotlight work properly
									>
										<ExtensibleCrossProductSearchDialog
											{...dialogProps}
											cloudId={cloudId}
											aggregatorUrl={aggregatorUrl}
											// @ts-expect-error - TS2322 - Type 'MutableRefObject<unknown> | ((instance: SearchProps | null) => void)' is not assignable to type 'HTMLInputElementRef'.
											forwardRef={forwardedInputRef || inputRef}
											confluenceReactivationExperiment={{
												isEligibleInactiveUser,
												isEligibleNewUser,
												nudgeShown,
												setAllowConfluenceAccessCache: setAllowConfluenceAccessCacheReactivation,
											}}
											confluenceAvailabilityExperiment={{
												isEligible: isConfluenceAvailabilityExperimentEligible,
												isTabClicked: isConfluenceAvailabilityExperimentTabClicked,
												setAllowConfluenceAccessCache,
											}}
										/>
									</div>
								</SpotlightTarget>
								{!__SERVER__ ? <EngagementSpotlight engagementId={SEARCH_SPOTLIGHT_KEY} /> : null}
							</SearchInputOverrider>
						</SearchInputWrapper>
					)}
				</ClientProvider>
			</JsErrorBoundary>
		);
	},
);

const SearchWithErrorBoundary = (props: SearchProps) => (
	<TopLevelErrorBoundary id={NAVIGATION_ITEM_ID.SEARCH}>
		<Search {...props} />
	</TopLevelErrorBoundary>
);

export { SearchWithErrorBoundary as Search };

const SearchInputWrapperDI = (props: React.PropsWithChildren) => {
	if (getWillShowNav4()) {
		return (
			<div css={[styles.searchInputWrapper, styles.nav4SearchInputWrapper]}>{props.children}</div>
		);
	}
	return (
		<div
			css={styles.searchInputWrapper}
			data-vc={`search-input-wrapper${__SERVER__ ? '-ssr' : ''}`}
		>
			{props.children}
		</div>
	);
};
const SearchInputOverriderDI = (props: React.PropsWithChildren) => {
	if (getWillShowNav4()) {
		return (
			<div css={[styles.searchInputOverrider, styles.nav4SearchInputOverrider]}>
				{props.children}
			</div>
		);
	}
	return <div css={styles.searchInputOverrider}>{props.children}</div>;
};
